"use client";

import Image from "next/image";
import { useState } from "react";
import { cfImage, rawImage } from "@/lib/img";

/**
 * A listing-gallery photo requested at the size it is actually rendered.
 *
 * Why this exists: `next.config.ts` sets `images.unoptimized: true`, which makes
 * every `sizes=` prop on <Image> inert — no `srcset` is emitted, so the hero
 * gallery shipped the RAW R2 originals (2834×3543, ~10 MP, ~38 MB decoded each,
 * and served as `application/octet-stream` so Cloudflare never edge-caches
 * them). The mobile carousel alone eager-loaded eight of those into an 88vw box.
 *
 * `cfImage()` asks Cloudflare for a resized variant instead (same mechanism
 * already proven on the listing cards). On error we fall back ONCE to the raw
 * URL, because transforms are metered and a 429 must never leave a blank frame
 * — the grey `bg-[#d9e0eb]` placeholder with no `onError` is exactly how the
 * Photo-tour blank-tile bug looked to guests.
 *
 * The swap is done via state, not by mutating `img.src`: next/image controls the
 * `src` prop and would clobber a raw DOM write on the next render.
 */
export default function GalleryImage({
  url,
  alt,
  width,
  sizes,
  priority = false,
  className = "object-cover",
}: {
  url: string;
  alt: string;
  /** Rendered CSS width — cfImage applies its own DPR bump on top. */
  width: number;
  sizes: string;
  priority?: boolean;
  className?: string;
}) {
  const [src, setSrc] = useState(() => cfImage(url, { width }));
  return (
    <Image
      src={src}
      alt={alt}
      fill
      sizes={sizes}
      priority={priority}
      loading={priority ? undefined : "lazy"}
      onError={() => {
        const raw = rawImage(src);
        if (raw !== src) setSrc(raw);
      }}
      className={className}
    />
  );
}
